//#region libraries
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

// external libraries
import { Title, Subtitle, Flex, Toggle, ToggleItem } from '@tremor/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// custom components
import { NewScreen, Card, CustomSpinner } from '_atoms';
import { TableHeader } from '_molecules';

// bootstrap
import { Button, Form, Modal, Row, Col, Table } from 'react-bootstrap';

// services
import {
  getLegacyAppBookings,
  rejectLegacyAppBooking,
  saveLegacyAppBookingNote,
  confirmLegacyAppBookingAirports,
} from '_services/pax-desk-service';

// hooks
import useIdleLogout from '../../../hooks/use-idle-logout';

// styles
import { Colors } from '_styles';

import LegacyRow from './legacy-row';
import DeskPagination from './pagination';
import { defaultRange, validateRange, MAX_RANGE_DAYS } from './format';
//#endregion

const DEFAULT_PAGE_SIZE = 50;
const AUTO_REFRESH_MS = 60 * 1000;
const IDLE_LOGOUT_MS = 6 * 60 * 60 * 1000;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_SEARCH_LENGTH = 60;

// List of bookings made in the legacy passenger app, so the desk can keep an eye on them while
// passengers move to the new app. Airports confirm these in the portal; the desk can also confirm
// both airports at once, reject a booking, or add an internal note.
const EMPTY_TEXT = {
  open: 'No old app bookings awaiting an airport for these dates.',
  confirmed: 'No confirmed old app bookings for these dates.',
  rejected: 'No rejected old app bookings for these dates.',
};

const MAX_NOTE_LENGTH = 1000;

const LegacyAppBookings = () => {
  const history = useHistory();

  // shared-PC hardening: log out after 15 minutes without activity
  useIdleLogout(IDLE_LOGOUT_MS);

  //#region state
  const [view, setView] = useState('open');
  const [range, setRange] = useState(defaultRange);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [bookings, setBookings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [expandedNotes, setExpandedNotes] = useState({}); // { [id]: true }
  const [rejecting, setRejecting] = useState(null); // booking awaiting the reject dialog
  const [rejectPending, setRejectPending] = useState({}); // { [id]: true }
  const [confirmFor, setConfirmFor] = useState(null); // booking awaiting the confirm dialog
  const [confirmPending, setConfirmPending] = useState({}); // { [id]: true }
  const [noteFor, setNoteFor] = useState(null); // booking whose note is being edited
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  //#endregion

  const rangeError = validateRange(range.from, range.to);
  const latestFetch = useRef(0);
  const loggingOut = useRef(false);
  const tableTop = useRef(null);

  // 401/403: the session is no longer valid for the desk — sign out
  const handleAuthError = useCallback((e) => {
    if (e?.status === 401 || e?.status === 403) {
      if (!loggingOut.current) {
        loggingOut.current = true;
        history.push('/logout');
      }
      return true;
    }
    return false;
  }, [history]);

  //#region data
  const fetchBookings = useCallback(async ({ quiet = false } = {}) => {
    if (rangeError) return;
    const fetchId = ++latestFetch.current;
    if (!quiet) setLoading(true);

    try {
      const result = await getLegacyAppBookings({
        view,
        from: range.from,
        to: range.to,
        q: search || undefined,
        limit: pageSize,
        skip,
      });
      if (fetchId !== latestFetch.current) return; // a newer fetch has started
      // the list shrank under us — step back to the last page that has rows
      if (skip > 0 && skip >= result.total) {
        setSkip(Math.max(0, Math.floor((result.total - 1) / pageSize) * pageSize));
        return;
      }
      setBookings(result.requests);
      setTotal(result.total);
      setError(null);
      setLastUpdated(new Date());
    } catch (e) {
      if (fetchId !== latestFetch.current) return;
      if (handleAuthError(e)) return;
      if (e?.status === 429) {
        toast.warn('Too many requests, wait a moment.');
        if (!quiet) setError('Too many requests, wait a moment.');
      } else if (!quiet) {
        setError(e?.message || 'Could not load old app bookings.');
      }
    } finally {
      if (fetchId === latestFetch.current && !quiet) setLoading(false);
    }
  }, [view, range.from, range.to, search, skip, pageSize, rangeError, handleAuthError]);

  // load on filter change
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setSkip(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // auto-refresh every 60s while the tab is visible; refresh on return to the tab
  useEffect(() => {
    let interval = null;
    const start = () => {
      clearInterval(interval);
      interval = setInterval(() => fetchBookings({ quiet: true }), AUTO_REFRESH_MS);
    };
    const onVisibility = () => {
      if (document.hidden) {
        clearInterval(interval);
      } else {
        fetchBookings({ quiet: true });
        start();
      }
    };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchBookings]);
  //#endregion

  //#region reject
  const submitReject = async () => {
    if (!rejecting) return;
    const booking = rejecting;
    setRejecting(null);
    if (rejectPending[booking.id]) return;
    setRejectPending((prev) => ({ ...prev, [booking.id]: true }));

    try {
      const result = await rejectLegacyAppBooking(booking.id);
      if (result?.alreadyRejected) {
        toast.info('Already rejected');
      } else {
        toast.success(`Rejected booking for ${booking.name || 'passenger'}`);
      }
      // it no longer belongs on Awaiting airport
      setBookings((prev) => prev.filter((b) => b.id !== booking.id));
      setTotal((prev) => Math.max(0, prev - 1));
      fetchBookings({ quiet: true });
    } catch (e) {
      if (handleAuthError(e)) return;
      if (e?.status === 429) {
        toast.warn('Too many requests, wait a moment.');
      } else {
        toast.error(e?.message || 'Could not reject, please try again.');
      }
      if (e?.status === 404) fetchBookings({ quiet: true });
    } finally {
      setRejectPending((prev) => {
        const next = { ...prev };
        delete next[booking.id];
        return next;
      });
    }
  };
  //#endregion

  //#region confirm airports
  const submitConfirm = async () => {
    if (!confirmFor) return;
    const booking = confirmFor;
    setConfirmFor(null);
    if (confirmPending[booking.id]) return;
    setConfirmPending((prev) => ({ ...prev, [booking.id]: true }));

    try {
      const result = await confirmLegacyAppBookingAirports(booking.id);
      if (result?.alreadyConfirmed) {
        toast.info('Airports were already confirmed');
      } else {
        toast.success(`Airports confirmed for ${booking.name || 'passenger'}`);
      }
      // it no longer belongs on Awaiting airport
      setBookings((prev) => prev.filter((b) => b.id !== booking.id));
      setTotal((prev) => Math.max(0, prev - 1));
      fetchBookings({ quiet: true });
    } catch (e) {
      if (handleAuthError(e)) return;
      if (e?.status === 429) {
        toast.warn('Too many requests, wait a moment.');
      } else {
        toast.error(e?.message || 'Could not confirm, please try again.');
      }
      if (e?.status === 404) fetchBookings({ quiet: true });
    } finally {
      setConfirmPending((prev) => {
        const next = { ...prev };
        delete next[booking.id];
        return next;
      });
    }
  };

  // the airports this booking still needs confirmed, for the dialog
  const airportsToConfirm = (booking) =>
    [
      booking.required?.origin && !booking.originConfirmed ? booking.origin?.iata : null,
      booking.required?.destination && !booking.destinationConfirmed ? booking.destination?.iata : null,
    ].filter(Boolean);
  //#endregion

  //#region internal note
  const openNote = (booking) => {
    setNoteFor(booking);
    setNoteText(booking.internalNote?.text || '');
  };

  const saveNote = async () => {
    if (!noteFor || noteSaving) return;
    setNoteSaving(true);
    try {
      const result = await saveLegacyAppBookingNote(noteFor.id, noteText.trim());
      const updated = result?.request;
      if (updated && updated.id) {
        setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
      }
      toast.success(noteText.trim() ? 'Note saved' : 'Note removed');
      setNoteFor(null);
    } catch (e) {
      if (handleAuthError(e)) return;
      if (e?.status === 429) {
        toast.warn('Too many requests, wait a moment.');
      } else {
        toast.error(e?.message || 'Could not save the note, please try again.');
      }
    } finally {
      setNoteSaving(false);
    }
  };
  //#endregion

  //#region handlers
  const changeView = (value) => {
    setView(value);
    setSkip(0);
  };

  const changeRange = (key, value) => {
    setRange((prev) => ({ ...prev, [key]: value }));
    setSkip(0);
  };

  const changeSkip = (value) => {
    setSkip(value);
    tableTop.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const changePageSize = (value) => {
    // keep the first visible row on screen
    setSkip(Math.floor(skip / value) * value);
    setPageSize(value);
  };

  const toggleNotes = (id) => {
    setExpandedNotes((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  //#endregion

  const pager = (
    <DeskPagination
      total={total}
      skip={skip}
      pageSize={pageSize}
      shown={bookings.length}
      disabled={loading}
      onSkipChange={changeSkip}
      onPageSizeChange={changePageSize}
      noun="booking"
    />
  );

  return (
    <NewScreen>
      <div className="mt-3" style={{ paddingBottom: 22 }}>
        <Card>
          {/* title + awaiting / confirmed */}
          <Flex>
            <div>
              <Title>Old App Bookings</Title>
              <Subtitle>
                Bookings made in the old passenger app, while passengers move to the new one. Airports confirm these in the portal, or the desk can confirm both airports here.
              </Subtitle>
            </div>
            <div>
              <Toggle color="zinc" defaultValue={view} onValueChange={changeView}>
                <ToggleItem value="open" text="Awaiting airport" />
                <ToggleItem value="confirmed" text="Confirmed" />
                <ToggleItem value="rejected" text="Rejected" />
              </Toggle>
            </div>
          </Flex>

          {/* filters */}
          <Row className="mt-3 g-2 align-items-end">
            <Col xs={12} md={4}>
              <Form.Label style={styles.label}>Search</Form.Label>
              <Form.Control
                type="search"
                placeholder="Name, flight or reference"
                value={searchInput}
                maxLength={MAX_SEARCH_LENGTH}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label style={styles.label}>From</Form.Label>
              <Form.Control
                type="date"
                value={range.from}
                onChange={(e) => changeRange('from', e.target.value)}
                isInvalid={!!rangeError}
              />
            </Col>
            <Col xs={6} md={2}>
              <Form.Label style={styles.label}>To</Form.Label>
              <Form.Control
                type="date"
                value={range.to}
                onChange={(e) => changeRange('to', e.target.value)}
                isInvalid={!!rangeError}
              />
            </Col>
            <Col xs={12} md="auto">
              <Button variant="outline-primary" onClick={() => fetchBookings()} disabled={loading || !!rangeError}>
                {loading ? <CustomSpinner /> : 'Refresh'}
              </Button>
            </Col>
            <Col xs={12} md style={{ textAlign: 'right' }}>
              {lastUpdated ? (
                <span style={styles.small}>
                  {`Updated ${lastUpdated.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit' })} · refreshes every minute`}
                </span>
              ) : null}
            </Col>
          </Row>
          {rangeError ? (
            <div style={styles.rangeError}>{rangeError}</div>
          ) : (
            <div style={styles.small}>{`Flights between the chosen dates (maximum ${MAX_RANGE_DAYS} days).`}</div>
          )}

          {/* table */}
          <div ref={tableTop} className="mt-3" />
          {bookings.length > 0 && !error ? pager : null}
          <div style={{ overflowX: 'auto', minHeight: 200 }}>
            {loading && bookings.length === 0 ? (
              <div className="d-flex flex-column align-items-center mt-5" style={{ opacity: 0.6 }}>
                <CustomSpinner />
                <p className="mt-3" style={{ color: Colors.PRM_LIGHT_BLUE }}>Getting old app bookings</p>
              </div>
            ) : error ? (
              <div className="d-flex flex-column align-items-center mt-5">
                <p style={{ color: '#C0392B' }}>{error}</p>
                <Button variant="outline-primary" size="sm" onClick={() => fetchBookings()}>Try again</Button>
              </div>
            ) : bookings.length === 0 ? (
              <div className="d-flex flex-column align-items-center mt-5" style={{ opacity: 0.6 }}>
                <p>{EMPTY_TEXT[view]}</p>
              </div>
            ) : (
              <Table hover responsive style={{ opacity: loading ? 0.6 : 1 }}>
                <thead>
                  <tr>
                    <th><TableHeader title="Passenger" /></th>
                    <th><TableHeader title="Notes" /></th>
                    <th><TableHeader title="Flight" /></th>
                    <th><TableHeader title="Booking" /></th>
                    <th><TableHeader title="Departure" /></th>
                    <th><TableHeader title="Arrival" /></th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <LegacyRow
                      key={booking.id}
                      booking={booking}
                      expanded={!!expandedNotes[booking.id]}
                      onToggleNotes={toggleNotes}
                      onReject={setRejecting}
                      rejecting={!!rejectPending[booking.id]}
                      onEditNote={openNote}
                      onConfirm={setConfirmFor}
                      confirming={!!confirmPending[booking.id]}
                    />
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* pagination */}
          {bookings.length > 0 && !error ? pager : null}
        </Card>
      </div>

      {/* confirm airports dialog */}
      <Modal show={!!confirmFor} onHide={() => setConfirmFor(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm airports</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmFor
            ? `Confirm ${airportsToConfirm(confirmFor).join(' and ') || 'the airports'} for ${confirmFor.name || 'this passenger'}${confirmFor.referenceNumber ? ` (${confirmFor.referenceNumber})` : ''}?`
            : ''}
          <div style={{ ...styles.small, marginTop: 10 }}>
            This can’t be undone. It works like an airport confirming in the portal: the airports get the SITA message and the passenger is notified.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmFor(null)}>Cancel</Button>
          <Button variant="primary" onClick={submitConfirm}>Confirm</Button>
        </Modal.Footer>
      </Modal>

      {/* internal note dialog */}
      <Modal show={!!noteFor} onHide={() => !noteSaving && setNoteFor(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{`Internal note${noteFor?.name ? ` · ${noteFor.name}` : ''}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={5}
            value={noteText}
            maxLength={MAX_NOTE_LENGTH}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Only the desk can see this"
            autoFocus
          />
          <div style={{ ...styles.small, marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
            <span>For the desk only. The passenger never sees this note. Leave it empty to remove it.</span>
            <span>{`${noteText.length}/${MAX_NOTE_LENGTH}`}</span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setNoteFor(null)} disabled={noteSaving}>Cancel</Button>
          <Button variant="primary" onClick={saveNote} disabled={noteSaving}>
            {noteSaving ? 'Saving…' : 'Save note'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* reject dialog */}
      <Modal show={!!rejecting} onHide={() => setRejecting(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Reject booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {rejecting
            ? `Reject the old app booking for ${rejecting.name || 'this passenger'}${rejecting.referenceNumber ? ` (${rejecting.referenceNumber})` : ''}?`
            : ''}
          <div style={{ ...styles.small, marginTop: 10 }}>
            It moves to the Rejected tab. Airports still see it in the portal, and the passenger isn’t told.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setRejecting(null)}>Cancel</Button>
          <Button variant="danger" onClick={submitReject}>Reject</Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={true} />
    </NewScreen>
  );
};

const styles = {
  label: {
    fontSize: 13,
    opacity: 0.7,
    marginBottom: 2,
  },
  small: {
    fontSize: 12,
    opacity: 0.6,
  },
  rangeError: {
    fontSize: 12,
    color: '#C0392B',
    marginTop: 4,
  },
};

export default LegacyAppBookings;
